// /src/pages/ProyectoDetalle.tsx
import React, { useState } from 'react';
import {
  Box,
  Heading,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Select,
  Textarea,
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  useDisclosure,
  VStack,
  HStack,
  Text,
  Badge,
  Input,
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  useToast,
} from '@chakra-ui/react';
import { useParams } from 'react-router-dom';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import { IProyecto, ILineamiento, IComentario } from '../types';
import { mockProyectos } from '../mocks/proyectos';
import { mockEstandares } from '../mocks/estandares';
import { useAuth } from '../hooks/useAuth';

export const ProyectoDetalle: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { usuario } = useAuth();
  const [proyecto, setProyecto] = useState<IProyecto | null>(
    mockProyectos.find(p => p.id === id) || null
  );
  const [lineamientoSeleccionado, setLineamientoSeleccionado] = useState<ILineamiento | null>(null);
  const [nuevoComentario, setNuevoComentario] = useState('');
  const { isOpen: isEvidenciaOpen, onOpen: onEvidenciaOpen, onClose: onEvidenciaClose } = useDisclosure();
  const { isOpen: isComentariosOpen, onOpen: onComentariosOpen, onClose: onComentariosClose } = useDisclosure();
  const { isOpen: isReporteOpen, onOpen: onReporteOpen, onClose: onReporteClose } = useDisclosure();
  const toast = useToast();

  if (!proyecto) {
    return (
      <Box>
        <Heading>Proyecto no encontrado</Heading>
      </Box>
    );
  }

  const estandar = mockEstandares.find(e => e.id === proyecto.estandarId);

  // Calcular estadísticas
  const totalLineamientos = proyecto.lineamientos.length;
  const cumple = proyecto.lineamientos.filter(l => l.estado === 'Cumple').length;
  const noCumple = proyecto.lineamientos.filter(l => l.estado === 'No Cumple').length;
  const enRevision = proyecto.lineamientos.filter(l => l.estado === 'En Revisión').length;
  const noAplica = proyecto.lineamientos.filter(l => l.estado === 'No Aplica').length;
  const porcentajeCumplimiento = totalLineamientos > 0 ? Math.round((cumple / totalLineamientos) * 100) : 0;
  const evidenciasCargadas = proyecto.lineamientos.filter(l => l.evidencia).length;

  // Datos para el gráfico
  const chartData = [
    { name: 'Cumple', value: cumple, color: '#48BB78' },
    { name: 'No Cumple', value: noCumple, color: '#F56565' },
    { name: 'En Revisión', value: enRevision, color: '#ECC94B' },
    { name: 'No Aplica', value: noAplica, color: '#CBD5E0' },
  ];

  const handleEstadoChange = (lineamientoId: string, nuevoEstado: string) => {
    setProyecto(prev => {
      if (!prev) return null;
      return {
        ...prev,
        lineamientos: prev.lineamientos.map(l =>
          l.id === lineamientoId ? { ...l, estado: nuevoEstado as any } : l
        ),
      };
    });
  };

  const handleCampoChange = (lineamientoId: string, campo: string, valor: string) => {
    setProyecto(prev => {
      if (!prev) return null;
      return {
        ...prev,
        lineamientos: prev.lineamientos.map(l =>
          l.id === lineamientoId ? { ...l, [campo]: valor } : l
        ),
      };
    });
  };

  const handleAbrirComentarios = (lineamiento: ILineamiento) => {
    setLineamientoSeleccionado(lineamiento);
    onComentariosOpen();
  };

  const handleAgregarComentario = () => {
    if (!nuevoComentario.trim() || !lineamientoSeleccionado) return;

    const comentario: IComentario = {
      usuario: usuario?.nombre || 'Usuario',
      texto: nuevoComentario,
      fecha: new Date().toLocaleDateString('es-ES'),
    };

    setProyecto(prev => {
      if (!prev) return null;
      return {
        ...prev,
        lineamientos: prev.lineamientos.map(l =>
          l.id === lineamientoSeleccionado.id
            ? { ...l, comentarios: [...l.comentarios, comentario] }
            : l
        ),
      };
    });

    setLineamientoSeleccionado(prev => 
      prev ? { ...prev, comentarios: [...prev.comentarios, comentario] } : null
    );

    setNuevoComentario('');
    
    toast({
      title: 'Comentario agregado',
      status: 'success',
      duration: 2000,
    });
  };

  const handleCargarEvidencia = () => {
    if (!lineamientoSeleccionado) return;

    const nombreArchivo = `evidencia_${lineamientoSeleccionado.id}_${Date.now()}.pdf`;
    
    setProyecto(prev => {
      if (!prev) return null;
      return {
        ...prev,
        lineamientos: prev.lineamientos.map(l =>
          l.id === lineamientoSeleccionado.id
            ? { ...l, evidencia: nombreArchivo }
            : l
        ),
      };
    });

    toast({
      title: 'Evidencia cargada',
      description: nombreArchivo,
      status: 'success',
      duration: 3000,
    });

    onEvidenciaClose();
  };

  return (
    <Box>
      {/* Header del Proyecto */}
      <Box mb={6}>
        <Heading size="lg" mb={2}>{proyecto.nombreEmpresa}</Heading>
        <Text color="gray.600">{proyecto.nombreEstandar}</Text>
      </Box>

      {/* Dashboard de Progreso */}
      <SimpleGrid columns={[1, 2, 4]} spacing={6} mb={8}>
        <Box bg="white" p={6} borderRadius="lg" boxShadow="sm">
          <Stat>
            <StatLabel>Cumplimiento</StatLabel>
            <StatNumber color="blue.600">{porcentajeCumplimiento}%</StatNumber>
            <StatHelpText>{cumple} de {totalLineamientos}</StatHelpText>
          </Stat>
        </Box>

        <Box bg="white" p={6} borderRadius="lg" boxShadow="sm">
          <Stat>
            <StatLabel>Tareas Pendientes</StatLabel>
            <StatNumber color="red.600">{noCumple}</StatNumber>
            <StatHelpText>No cumplen</StatHelpText>
          </Stat>
        </Box>

        <Box bg="white" p={6} borderRadius="lg" boxShadow="sm">
          <Stat>
            <StatLabel>En Revisión</StatLabel>
            <StatNumber color="yellow.600">{enRevision}</StatNumber>
            <StatHelpText>Pendientes</StatHelpText>
          </Stat>
        </Box>

        <Box bg="white" p={6} borderRadius="lg" boxShadow="sm">
          <Stat>
            <StatLabel>Evidencias</StatLabel>
            <StatNumber color="green.600">{evidenciasCargadas}</StatNumber>
            <StatHelpText>Archivos cargados</StatHelpText>
          </Stat>
        </Box>
      </SimpleGrid>

      {/* Gráfico de Progreso */}
      <Box bg="white" p={6} borderRadius="lg" boxShadow="sm" mb={8}>
        <Heading size="md" mb={4}>Distribución de Estados</Heading>
        <ResponsiveContainer width="100%" height={300}>
          <PieChart>
            <Pie
              data={chartData}
              cx="50%"
              cy="50%"
              labelLine={false}
              label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
              outerRadius={80}
              fill="#8884d8"
              dataKey="value"
            >
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Pie>
            <Tooltip />
            <Legend />
          </PieChart>
        </ResponsiveContainer>
      </Box>

      {/* Botón de Reporte */}
      <Box mb={6}>
        <Button colorScheme="purple" onClick={onReporteOpen}>
          📄 Generar Reporte (PDF)
        </Button>
      </Box>

      {/* Checklist por Capítulos */}
      <Box bg="white" p={6} borderRadius="lg" boxShadow="sm">
        <Heading size="md" mb={4}>Auditoría / Checklist</Heading>
        
        {estandar && (
          <Accordion allowMultiple>
            {estandar.capitulos.map((capitulo) => {
              const lineamientosCapitulo = proyecto.lineamientos.filter(l =>
                capitulo.lineamientos.some(cl => cl.id === l.id)
              );

              return (
                <AccordionItem key={capitulo.id} border="none" mb={4}>
                  <AccordionButton
                    bg="blue.50"
                    _hover={{ bg: 'blue.100' }}
                    borderRadius="md"
                    px={4}
                    py={3}
                  >
                    <Box flex="1" textAlign="left">
                      <HStack>
                        <Badge colorScheme="blue">{lineamientosCapitulo.length}</Badge>
                        <Text fontWeight="bold">{capitulo.nombre}</Text>
                      </HStack>
                    </Box>
                    <AccordionIcon />
                  </AccordionButton>
                  <AccordionPanel pb={4} pt={4}>
                    <Box overflowX="auto">
                      <Table variant="simple" size="sm">
                        <Thead>
                          <Tr>
                            <Th>Lineamiento</Th>
                            <Th>Estado</Th>
                            <Th>Hallazgo</Th>
                            <Th>Recomendación</Th>
                            <Th>Evidencia</Th>
                            <Th>Acciones</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {lineamientosCapitulo.map((lineamiento) => (
                            <Tr key={lineamiento.id}>
                              <Td maxW="200px">
                                <Text fontWeight="medium" fontSize="sm" noOfLines={2}>
                                  {lineamiento.nombre}
                                </Text>
                                <Text fontSize="xs" color="gray.600" noOfLines={1}>
                                  {lineamiento.descripcion}
                                </Text>
                              </Td>
                              <Td>
                                <Select
                                  size="sm"
                                  value={lineamiento.estado}
                                  onChange={(e) => handleEstadoChange(lineamiento.id, e.target.value)}
                                  isDisabled={usuario?.rol === 'Cliente'}
                                >
                                  <option value="No Aplica">No Aplica</option>
                                  <option value="Cumple">Cumple</option>
                                  <option value="No Cumple">No Cumple</option>
                                  <option value="En Revisión">En Revisión</option>
                                </Select>
                              </Td>
                              <Td>
                                <Textarea
                                  size="sm"
                                  value={lineamiento.hallazgo}
                                  onChange={(e) => handleCampoChange(lineamiento.id, 'hallazgo', e.target.value)}
                                  placeholder="Hallazgo..."
                                  rows={2}
                                  isDisabled={usuario?.rol === 'Cliente'}
                                />
                              </Td>
                              <Td>
                                <Textarea
                                  size="sm"
                                  value={lineamiento.recomendacion}
                                  onChange={(e) => handleCampoChange(lineamiento.id, 'recomendacion', e.target.value)}
                                  placeholder="Recomendación..."
                                  rows={2}
                                  isDisabled={usuario?.rol === 'Cliente'}
                                />
                              </Td>
                              <Td>
                                <VStack spacing={2}>
                                  {lineamiento.evidencia && (
                                    <Text fontSize="xs" color="green.600">
                                      ✓ {lineamiento.evidencia}
                                    </Text>
                                  )}
                                  <Button
                                    size="xs"
                                    colorScheme="teal"
                                    onClick={() => {
                                      setLineamientoSeleccionado(lineamiento);
                                      onEvidenciaOpen();
                                    }}
                                  >
                                    Cargar
                                  </Button>
                                </VStack>
                              </Td>
                              <Td>
                                <Button
                                  size="xs"
                                  colorScheme="purple"
                                  variant="outline"
                                  onClick={() => handleAbrirComentarios(lineamiento)}
                                >
                                  💬 ({lineamiento.comentarios.length})
                                </Button>
                              </Td>
                            </Tr>
                          ))}
                        </Tbody>
                      </Table>
                    </Box>
                  </AccordionPanel>
                </AccordionItem>
              );
            })}
          </Accordion>
        )}
      </Box>

      {/* Modal de Evidencia */}
      <Modal isOpen={isEvidenciaOpen} onClose={onEvidenciaClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Cargar Evidencia</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text mb={4}>
              Lineamiento: <strong>{lineamientoSeleccionado?.nombre}</strong>
            </Text>
            <Text fontSize="sm" color="gray.600" mb={4}>
              En un sistema real, aquí podrías seleccionar un archivo. Por ahora, simularemos la carga.
            </Text>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onEvidenciaClose}>
              Cancelar
            </Button>
            <Button colorScheme="teal" onClick={handleCargarEvidencia}>
              Simular Carga
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Drawer de Comentarios */}
      <Drawer isOpen={isComentariosOpen} placement="right" onClose={onComentariosClose} size="md">
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader>Comentarios</DrawerHeader>
          <DrawerBody>
            <Text fontWeight="bold" mb={4}>
              {lineamientoSeleccionado?.nombre}
            </Text>

            <VStack spacing={4} align="stretch" mb={6}>
              {lineamientoSeleccionado?.comentarios.map((comentario, index) => (
                <Box key={index} p={4} bg="gray.50" borderRadius="md">
                  <HStack justify="space-between" mb={2}>
                    <Text fontWeight="semibold" fontSize="sm">{comentario.usuario}</Text>
                    <Text fontSize="xs" color="gray.600">{comentario.fecha}</Text>
                  </HStack>
                  <Text fontSize="sm">{comentario.texto}</Text>
                </Box>
              ))}

              {lineamientoSeleccionado?.comentarios.length === 0 && (
                <Text color="gray.500" textAlign="center">
                  No hay comentarios aún
                </Text>
              )}
            </VStack>

            <Box>
              <Textarea
                value={nuevoComentario}
                onChange={(e) => setNuevoComentario(e.target.value)}
                placeholder="Escribe un comentario..."
                mb={3}
              />
              <Button colorScheme="blue" w="full" onClick={handleAgregarComentario}>
                Agregar Comentario
              </Button>
            </Box>
          </DrawerBody>
        </DrawerContent>
      </Drawer>

      {/* Modal de Reporte */}
      <Modal isOpen={isReporteOpen} onClose={onReporteClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Reporte de Hallazgos</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <Box>
                <Text fontWeight="bold" mb={2}>Resumen del Proyecto</Text>
                <Text fontSize="sm">Empresa: {proyecto.nombreEmpresa}</Text>
                <Text fontSize="sm">Estándar: {proyecto.nombreEstandar}</Text>
                <Text fontSize="sm">Cumplimiento: {porcentajeCumplimiento}%</Text>
              </Box>

              <Box>
                <Text fontWeight="bold" mb={2}>Hallazgos (No Cumple)</Text>
                {proyecto.lineamientos
                  .filter(l => l.estado === 'No Cumple' && l.hallazgo)
                  .map((lineamiento) => (
                    <Box key={lineamiento.id} p={3} bg="red.50" borderRadius="md" mb={2}>
                      <Text fontWeight="semibold" fontSize="sm" mb={1}>
                        {lineamiento.nombre}
                      </Text>
                      <Text fontSize="sm" color="gray.700">
                        <strong>Hallazgo:</strong> {lineamiento.hallazgo}
                      </Text>
                      {lineamiento.recomendacion && (
                        <Text fontSize="sm" color="gray.700" mt={1}>
                          <strong>Recomendación:</strong> {lineamiento.recomendacion}
                        </Text>
                      )}
                    </Box>
                  ))}
                
                {proyecto.lineamientos.filter(l => l.estado === 'No Cumple' && l.hallazgo).length === 0 && (
                  <Text color="gray.500">No hay hallazgos registrados</Text>
                )}
              </Box>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="blue" onClick={onReporteClose}>
              Cerrar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};
